// Variant configs for the `Input` and `TextArea` primitives, lifted into
// a pure data module so they can be unit-tested without loading Tamagui.
//
// Why this lives apart from `Input.tsx` / `TextArea.tsx`: Tamagui's
// runtime can't load in our Node test env (the libs/ui vitest config
// excludes `src/components/**` for the same reason). Keeping the
// variant config as plain objects lets a structural test assert that
// the `inline` variant paints no focus indicator — the exact regression
// that produced the double cerulean ring on row-embedded fields when
// Tamagui's default `focusStyle` / `focusVisibleStyle` leaked through.

// Tamagui's Input ships with `focusStyle: { borderColor:
// '$borderColorFocus' }` AND `focusVisibleStyle: { outlineColor,
// outlineWidth: 2, outlineStyle: 'solid' }` baked into its
// `unstyled: false` defaults. Every variant has to override BOTH —
// not just the base — because styled variants merge on top of the
// base. Anything less and the upstream ring leaks through.
export const NO_FOCUS_RING = {
  borderColor: 'transparent',
  outlineColor: 'transparent',
  outlineWidth: 0,
  outlineStyle: 'none' as never,
} as const;

const NO_OUTLINE_FOCUS_VISIBLE = {
  outlineColor: 'transparent',
  outlineWidth: 0,
  outlineStyle: 'none' as never,
} as const;

// Standalone variant — the input shows its own 2px focus border via the
// $borderFocus token (defaults to cerulean; ADR-0072). Outline-based
// focus indicator is zeroed so we get one ring, not a border + outline
// stacked on top.
export const DEFAULT_INPUT_VARIANT = {
  backgroundColor: '$surface',
  borderWidth: 2,
  borderColor: '$borderDefault',
  focusStyle: {
    borderColor: '$borderFocus',
    outlineColor: 'transparent',
    outlineWidth: 0,
    outlineStyle: 'none' as never,
  },
  focusVisibleStyle: NO_OUTLINE_FOCUS_VISIBLE,
} as const;

// Row-embedded variant — the wrapping row owns the focus visual, so
// the input itself must paint nothing in any focus state.
export const INLINE_INPUT_VARIANT = {
  backgroundColor: 'transparent',
  borderWidth: 2,
  borderColor: 'transparent',
  paddingHorizontal: 0,
  paddingVertical: 0,
  focusStyle: NO_FOCUS_RING,
  focusVisibleStyle: NO_FOCUS_RING,
} as const;

// Error and disabled boolean variants (ADR-0074) — composed with the
// base variant rather than replacing it. Setting `error` paints the
// border red and overrides focus to keep the error border visible
// while focused.
export const ERROR_INPUT_VARIANT_TRUE = {
  borderColor: '$borderError',
  focusStyle: {
    borderColor: '$borderError',
    outlineColor: 'transparent',
    outlineWidth: 0,
    outlineStyle: 'none' as never,
  },
} as const;

export const DISABLED_INPUT_VARIANT_TRUE = {
  backgroundColor: '$surfaceDisabled',
  borderColor: '$borderDisabled',
  color: '$textDisabled',
  // Block input interaction visually; focus suppressed.
  focusStyle: NO_FOCUS_RING,
} as const;
